import { useState } from 'react';
import { useAppDispatch } from '../../../Redux/hooks';
import { createLesson, postponeLesson, removeLesson, setLessonStatus } from '../../../Redux/lessonsSlice';
import { useToast } from '../../SharedArea/Toast/ToastProvider';
import { useTranslation } from '../../../i18n/useTranslation';
import { useDisplayName } from '../../../Utils/useDisplayName';
import type { TranslationKey } from '../../../i18n/he';
import type { LessonModel } from '../../../Models/LessonModel';
import { LessonStatus } from '../../../Models/enums';
import { Modal } from '../../SharedArea/Modal/Modal';
import { ConfirmDialog } from '../../SharedArea/ConfirmDialog/ConfirmDialog';
import { LessonEditor } from './LessonEditor';
import './LessonStatusSheet.css';

interface Choice {
  status: LessonStatus;
  label: TranslationKey;
  description: TranslationKey;
  className: string;
}

function labelFor(status: LessonStatus): TranslationKey {
  return CHOICES.find(choice => choice.status === status)?.label ?? 'unpaid';
}

const CHOICES: Choice[] = [
  { status: LessonStatus.Paid, label: 'paid', description: 'stPaidDesc', className: 'is-paid' },
  { status: LessonStatus.Unpaid, label: 'unpaid', description: 'stUnpaidDesc', className: 'is-unpaid' },
  { status: LessonStatus.CancelledCharged, label: 'stCancelledCharged', description: 'stCancelledChargedDesc', className: 'is-cancelled-charged' },
  { status: LessonStatus.CancelledFree, label: 'stCancelledFree', description: 'stCancelledFreeDesc', className: 'is-cancelled-free' }
];

/**
 * The picker opened from a lesson row.
 *
 * Postponing sits beside the four statuses because that is where a tutor
 * looks for it, but it is not one: it moves the lesson and resets it to
 * unpaid, since a lesson that has not happened yet is not a debt whatever it
 * was before.
 */
export function LessonStatusSheet({ lesson, onClose }: { lesson: LessonModel; onClose: () => void }) {
  const { tr } = useTranslation();
  const displayName = useDisplayName();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [postponing, setPostponing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [date, setDate] = useState(lesson.date);
  const [time, setTime] = useState(lesson.time ?? '');
  const [busy, setBusy] = useState(false);

  async function choose(status: LessonStatus) {
    if (status === lesson.status) {
      onClose();
      return;
    }
    setBusy(true);
    const previous = lesson.status;
    // unwrap, so a failed request closes the sheet without claiming the
    // status changed. A toast that says "changed to paid" over a lesson that
    // is still unpaid is worse than no toast.
    try {
      await dispatch(setLessonStatus({ id: lesson.id, status })).unwrap();
    } catch {
      onClose();
      toast({ message: tr('statusChangeError') });
      return;
    }
    onClose();
    toast({
      message: tr('statusChangedTo', tr(labelFor(status))),
      onUndo: () => { void dispatch(setLessonStatus({ id: lesson.id, status: previous })); }
    });
  }

  async function remove() {
    setBusy(true);
    try {
      await dispatch(removeLesson(lesson.id)).unwrap();
    } catch {
      onClose();
      toast({ message: tr('errorGeneric') });
      return;
    }
    onClose();
    toast({
      message: tr('lessonDeleted', displayName(lesson.student)),
      // Recreated rather than restored: the API has no undelete, so the
      // lesson comes back with a new id. Everything a tutor can see about it
      // is the same, which is what the offer promises.
      onUndo: () => {
        void dispatch(createLesson({
          student: lesson.student,
          date: lesson.date,
          amount: lesson.amount,
          status: lesson.status,
          ...(lesson.time ? { time: lesson.time } : { time: null })
        }));
      }
    });
  }

  async function savePostpone() {
    if (!date) return;
    setBusy(true);
    await dispatch(postponeLesson({ id: lesson.id, date, time: time || null }));
    onClose();
  }

  if (editingDetails) return <LessonEditor lesson={lesson} onClose={onClose} />;

  if (deleting) {
    return (
      <ConfirmDialog
        title={tr('deleteLessonTitle', displayName(lesson.student))}
        // Deleting a lesson removes it from the income record, which is not
        // the same as cancelling it. Cancelling is a status; this is not.
        body={tr('deleteLessonBody')}
        confirmLabel={tr('delete')}
        destructive
        onCancel={() => setDeleting(false)}
        onConfirm={() => void remove()}
      />
    );
  }

  if (postponing) {
    return (
      <Modal
        title={tr('postponeTitle', displayName(lesson.student))}
        subtitle={tr('postponeHint')}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="ghost-button" onClick={() => setPostponing(false)}>
              {tr('back')}
            </button>
            <button type="button" className="primary-button" onClick={savePostpone} disabled={busy || !date}>
              {tr('save')}
            </button>
          </>
        }
      >
        <div className="postpone-fields">
          <label className="field">
            <span>{tr('date')}</span>
            <input className="text-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </label>
          <label className="field">
            <span>{tr('time')}</span>
            <input className="text-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
          </label>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={tr('statusMenuTitle')} subtitle={displayName(lesson.student)} onClose={onClose}>
      <div className="status-options">
        {CHOICES.map(choice => (
          <button
            key={choice.status}
            type="button"
            className={`status-option ${choice.className}${choice.status === lesson.status ? ' is-current' : ''}`}
            onClick={() => void choose(choice.status)}
            disabled={busy}
            aria-current={choice.status === lesson.status}
          >
            <span className="status-option-dot" aria-hidden="true" />
            <span className="status-option-text">
              <span className="status-option-label">{tr(choice.label)}</span>
              <span className="status-option-desc">{tr(choice.description)}</span>
            </span>
          </button>
        ))}
        <button
          type="button"
          className="status-option is-edit"
          onClick={() => setEditingDetails(true)}
          disabled={busy}
        >
          <span className="status-option-dot" aria-hidden="true" />
          <span className="status-option-text">
            <span className="status-option-label">{tr('editLesson')}</span>
            <span className="status-option-desc">{tr('editLessonDesc')}</span>
          </span>
        </button>
        <button
          type="button"
          className="status-option is-postpone"
          onClick={() => setPostponing(true)}
          disabled={busy}
        >
          <span className="status-option-dot" aria-hidden="true" />
          <span className="status-option-text">
            <span className="status-option-label">{tr('stPostpone')}</span>
            <span className="status-option-desc">{tr('stPostponeDesc')}</span>
          </span>
        </button>
        {/* Deleting sits with the statuses because that is where a tutor
            looks for it, and apart from them because it is not one: a
            cancelled lesson stays on the record, a deleted one does not. */}
        <button
          type="button"
          className="status-option is-delete"
          onClick={() => setDeleting(true)}
          disabled={busy}
        >
          <span className="status-option-dot" aria-hidden="true" />
          <span className="status-option-text">
            <span className="status-option-label">{tr('delete')}</span>
            <span className="status-option-desc">{tr('deleteLessonDesc')}</span>
          </span>
        </button>
      </div>
    </Modal>
  );
}
